import React, { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import './wikiModals.css';
import useWikiRole from '../shared/useWikiRole';
import { wikiKeys } from '../shared/useWikiQueries';
import { parseWikiPath } from '../shared/wikiRoutes';
import { WIKI_TOASTS, useWikiUi, wikiUi } from '../shared/wikiUi';
import WikiCategoryModal from './WikiCategoryModal';
import WikiConfirmModal from './WikiConfirmModal';
import WikiMovePageModal from './WikiMovePageModal';
import WikiPageModal from './WikiPageModal';

const selectModal = (state) => state.modal;

// Each openModal() call stores a new modal object; key the modal component on
// it so every open starts from the prototype's fresh opener state.
const modalIds = new WeakMap();
let modalSeq = 0;
function modalKey(modal) {
    if (!modalIds.has(modal)) {
        modalSeq += 1;
        modalIds.set(modal, modalSeq);
    }
    return `${modal.kind}-${modalIds.get(modal)}`;
}

/** The category/subcategory the current route sits in (New page default location). */
export function routeLocationId(pathname, search, queryClient) {
    const route = parseWikiPath(pathname, search);
    if (!route) return null;
    if (route.view === 'category') return route.id || null;
    if (route.view === 'page' && queryClient) {
        const page = queryClient.getQueryData(wikiKeys.page(route.id));
        return (page && (page.subcategory_id || page.category_id)) || null;
    }
    return null;
}

/**
 * Every Wiki modal needs `canEdit`; a confirm opened with
 * `requires: 'admin'` (category archive, #872) also needs `canAdmin`.
 */
export function modalAllowed(modal, role) {
    if (!role || !role.canEdit) return false;
    if (modal && modal.kind === 'confirm' && modal.opts && modal.opts.requires === 'admin') {
        return Boolean(role.canAdmin);
    }
    return true;
}

/**
 * `<WikiModalHost/>` — mounted once by pages/WikiV2.jsx (only for readers).
 * Renders the create modal held in the `wikiUi` store:
 *   'category'     New category
 *   'subcategory'  New subcategory in {Parent}   opts.parentId
 *   'page'         New page / Import a page       opts.mode, categoryId,
 *                                                 subcategoryId, template
 *   'categorySettings'  Settings (#872)          opts.category
 *   'movePage'     Move a page (#872)             opts.page
 *   'confirm'      Archive confirmation (#872)    see WikiConfirmModal
 * Open with `wikiUi.openModal(kind, opts)`; every modal closes with
 * `wikiUi.closeModal()`.
 *
 * Role gate (Owen 2026-10-06: Editors create categories, subcategories and
 * pages; Members don't). The prototype modals had no checks of their own, so
 * this host refuses for Members whatever opened it: a page modal turns into
 * the prototype's Member toast, a category modal just closes (there is no
 * prototype copy for it). The API enforces the same rule.
 */
export default function WikiModalHost() {
    const modal = useWikiUi(selectModal);
    const role = useWikiRole();
    const location = useLocation();
    const queryClient = useQueryClient();
    const close = useCallback(() => wikiUi.closeModal(), []);

    const refused = Boolean(modal) && !role.loading && !modalAllowed(modal, role);
    useEffect(() => {
        if (!refused) return;
        const { kind } = wikiUi.getState().modal || {};
        wikiUi.closeModal();
        if (kind === 'page') wikiUi.toast(WIKI_TOASTS.memberCannotCreate);
    }, [refused]);

    if (!modal || role.loading || !modalAllowed(modal, role)) return null;

    const opts = modal.opts || {};
    if (modal.kind === 'page') {
        return (
            <WikiPageModal
                key={modalKey(modal)}
                opts={opts}
                routeLocationId={routeLocationId(location.pathname, location.search, queryClient)}
                onClose={close}
            />
        );
    }
    if (modal.kind === 'category' || modal.kind === 'subcategory' || modal.kind === 'categorySettings') {
        return <WikiCategoryModal key={modalKey(modal)} kind={modal.kind} opts={opts} onClose={close} />;
    }
    if (modal.kind === 'movePage') {
        return <WikiMovePageModal key={modalKey(modal)} opts={opts} onClose={close} />;
    }
    if (modal.kind === 'confirm') {
        return <WikiConfirmModal key={modalKey(modal)} opts={opts} onClose={close} />;
    }
    return null;
}
